import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { EmblaCarousel } from '../../components/Carousel/EmblaCarousel';
// import MultiplexAd from "../Ads/MultiplexAds";

import {
  collection,
  getDocs,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { db } from "../../firebase";
import "./BlogPage.css";
import PromoBanner from "../../components/Promos/PromoBanner";

const formatDate = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
};

const BlogPage = () => {
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- NEW STATE FOR FILTERING ---
  const [activeFilter, setActiveFilter] = useState('All'); // 'All' is the default

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const postsRef = collection(db, "blog_posts");

        /* --- FEATURED POSTS --- */
        const featuredQuery = query(
          postsRef,
          where("isFeatured", "==", true),
          orderBy("date", "desc")
        );

        const featuredSnapshot = await getDocs(featuredQuery);
        const featuredData = featuredSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));

        setFeaturedPosts(featuredData);

        /* --- REGULAR POSTS --- */
        const regularQuery = query(
          postsRef,
          where("isFeatured", "==", false),
          orderBy("date", "desc")
        );

        const regularSnapshot = await getDocs(regularQuery);
        const regularData = regularSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));

        setPosts(regularData);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
    window.scrollTo(0, 0);
  }, []);

  // --- Get unique categories for our filter chips ---
  const categories = useMemo(() => {
    const allCategories = posts.map(post => post.category);
    // Use Set to get unique values, then convert back to array
    return ['All', ...new Set(allCategories)];
  }, [posts]);

  // --- Filter the posts based on the active filter ---
  const filteredPosts = useMemo(() => {
    if (activeFilter === 'All') {
      return posts;
    }
    return posts.filter(post => post.category === activeFilter);
  }, [activeFilter, posts]);

  /* --- CAROUSEL TIPS --- */
  const tips = [
    {
      title: "Price It Smart",
      description: "Compare similar listings before posting to sell faster.",
      icon: "🏷️",
      color: "#f3e5f5"
    },
    {
      title: "Use Clear Photos",
      description: "Natural light + clean background increases trust.",
      icon: "📸",
      color: "#e3f2fd"
    },
    {
      title: "Write Honest Details",
      description: "Mention flaws upfront to avoid disputes.",
      icon: "📝",
      color: "#e8f5e9"
    },
    {
      title: "Meet in Public",
      description: "Choose busy locations for safe transactions.",
      icon: "☕",
      color: "#fff3e0"
    },
    {
      title: "Respond Quickly",
      description: "Fast replies improve your seller rating.",
      icon: "⚡",
      color: "#ede7f6"
    },
    {
      title: "Verify Buyers",
      description: "Check profile ratings before meeting.",
      icon: "✅",
      color: "#e0f7fa"
    },
    {
      title: "Bundle Items",
      description: "Offer small discounts for multiple purchases.",
      icon: "📦",
      color: "#fce4ec"
    },
    {
      title: "Keep Chat Inside App",
      description: "Avoid sharing personal numbers too early.",
      icon: "🔒",
      color: "#e0f2f1"
    },
    {
      title: "Refresh Old Listings",
      description: "Update photos or description to boost visibility.",
      icon: "🔄",
      color: "#fff8e1"
    }
  ];



  return (
    <div className="blog-page">
    
      {/* HERO HEADER */}
      <div className="blog-header-dynamic">
        <div className="container">
          <h1 className="section-title light-text">
            Welcome to The Qinto Hub
          </h1>
          <p className="section-sub-title">Your guide to mastering the secondhand marketplace.</p>

          {/* CONTINUOUS CAROUSEL */}
          <div className="tips-carousel">
            <div className="tips-carousel-track">
              {[...tips, ...tips, ...tips].map((tip, index) => (
                <div
                  key={`${tip.title}-${index}`}
                  className="tip-card"
                  style={{ backgroundColor: tip.color }}
                >
                  <span className="tip-icon">{tip.icon}</span>
                  <div className="tip-content">
                    <span className="tip-title">{tip.title}</span>
                    <span className="tip-description">
                      {tip.description}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="container blog-container">
        {loading ? (
          <div className="loading-skeleton">
            <div className="skeleton-card"></div>
            <div className="skeleton-card"></div>
            <div className="skeleton-card"></div>
          </div>
        ) : (
          <>
            {/* FEATURED SECTION */}
            {featuredPosts.length > 0 && (
              <section className="featured-section">
                <div className="section-header">
                  <h2>Featured Insights</h2>
                </div>

                {/* Using the Embla Carousel Component */}
                <EmblaCarousel>
                  {featuredPosts.map(post => (
                    <div className="embla__slide" key={post.id}>
                      <Link to={`/blog/${post.slug}`} className="featured-card">
                        <img src={post.image} alt={post.title} loading="lazy" className="featured-card-image" />
                        <div className="featured-card-overlay"></div>
                        <div className="featured-card-content">
                          <span className="featured-category">{post.category}</span>
                          <h3>{post.title}</h3>
                          {/* <div className="blog-meta">
                          <span className="blog-meta-item">
                            ⏱️ {post.readingTime ?? 0} min read
                          </span>
                          <span className="blog-meta-item">
                            📄 {post.wordCount?.toLocaleString() ?? 0} words
                          </span>
                        </div> */}
                        </div>
                      </Link>
                    </div>
                  ))}
                </EmblaCarousel>

              </section>
            )}

            <section className="featured-section">
              <div className="section-header">
                <h2>Latest Articles</h2>
              </div>

              {/* The Filter Chip Bar */}
              <div className="filter-chips-container">
                <div className="filter-chips">
                  {categories.map((category) => (
                    <button
                      key={category}
                      className={`chip ${activeFilter === category ? 'active' : ''}`}
                      onClick={() => setActiveFilter(category)}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* ALL POSTS GRID */}
              <div className="blog-grid">
                {filteredPosts.map((post) => (
                  <Link
                    to={`/blog/${post.slug}`}
                    key={post.id}
                    className="blog-card"
                  >
                    <div className="blog-card-image">
                      {post.image ? (
                        <img
                          src={post.image}
                          alt={post.title}
                          loading="lazy"
                        />
                      ) : (
                        <div className="placeholder-img">Qinto</div>
                      )}
                    </div>

                    <div className="blog-card-content">
                      <span className="blog-category">
                        {post.category}
                      </span>
                      <h3>{post.title}</h3>
                      <p>{post.excerpt}</p>

                       {/* NEW: reading time & word count */}
                    {/* <div className="blog-meta">
                      <span className="blog-meta-item">
                        ⏱️ {post.readingTime ?? 0} min read
                      </span>
                      <span className="blog-meta-item">
                        📄 {post.wordCount?.toLocaleString() ?? 0} words
                      </span>
                    </div> */}

                    {/* NEW: related keywords as tags */}
                    {post.relatedKeywords?.length > 0 && (
                      <div className="blog-tags">
                        <strong>Related topics:</strong>
                        {post.relatedKeywords.map((kw, i) => (
                          <span
                            key={i}
                            className="tag"
                          >
                            #{kw.replace(/\s+/g, "-").toLowerCase()}
                          </span>
                        ))}
                      </div>
                    )}

                      <div className="blog-footer">
                        <span className="blog-date">
                          {formatDate(post.date)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

            </section>


          </>
        )}

        

        {/* <div className="ad-label">Advertisement</div>

        <div className="multiplex-ad-container">
          <MultiplexAd />
        </div> */}

      </div>

      <div style={{ marginTop: '30px', marginBottom: '35px' }}>
          <PromoBanner/>
        </div>
    </div>
  );
};

export default BlogPage;